import { useApi, SKY_API_ENDPOINT } from '@/api';
import {
  TableSkeleton,
  CurrencyValue,
  PercentageValue,
  Flex,
  Text,
  Box,
  Heading
} from '@blockanalitica/ui';
import { MultipleKpiCard, Page, InfoTooltip, LinkBanner } from '@/components';

import ASCTable from './ASCTable';
import ASCGraph from './ASCGraph';
export default function ASCPage() {
  const [response, error] = useApi({
    baseUrl: SKY_API_ENDPOINT,
    url: `/asc/`
  });

  if (error) {
    throw error;
  }

  if (!response?.data) {
    return <TableSkeleton rows={10} columns={6} />;
  }
  const data = response.data;

  return (
    <Page title={'Actively Stabilizing Collateral Monitor'}>
      <LinkBanner
        text={"More About the Sky Protocol's ASC"}
        linkTo="https://sky-atlas.io/#A.3.3.1.3.2">
        <Box paddingTop="xs" paddingBottom="m" maxWidth="85%">
          <Heading size="2">
            About the Sky Protocol&apos;s Actively Stabilizing Collateral
          </Heading>
          <br />
          Actively Stabilizing Collateral are assets redeemable for USD and also
          stands for a system of allocation rules which ensures USDS peg
          stability and drives the Sky&apos;s internal rate system. ASC must be
          greater or equal to 25% of Sky&apos;s aggregate portfolio and consists
          of Resting and Latent. Resting ASC is in the form of on demand limit
          order, while Latent ASC are assets which can be automatically
          converted into Resting within a short time period.
        </Box>
      </LinkBanner>
      <MultipleKpiCard
        title="Total ASC"
        value={
          <CurrencyValue
            value={data.total_asc}
            variant="dynamic"
            options={{ currency: 'USD' }}
          />
        }
        footer={[
          {
            title: (
              <Flex gap="3xs">
                Resting ASC
                <InfoTooltip
                  text={
                    <Text size="3">
                      Resting Actively Stabilizing Collateral is currently
                      <br />
                      <br />
                      calculated as the sum of:
                      <br />
                      1. USDC in the LitePSM
                      <br />
                      2. USDC in the PSM3 on Base, Arbitrum, Unichain, Optimism
                      <br />
                      3. Cash Stablecoins in Curve (paired with USDS)
                      <br />
                      4. USDC in GUNI 0.01%
                      <br />
                      5. USDC in GUNI 0.05%
                    </Text>
                  }
                />
              </Flex>
            ),
            value: (
              <CurrencyValue
                value={data.total_resting_asc}
                options={{ currency: 'USD' }}
              />
            )
          },
          {
            title: (
              <Flex gap="3xs">
                Latent ASC / Max Latent ASC
                <InfoTooltip
                  text={
                    <Text size="3">
                      Latent Actively Stabilizing Collateral consist of USD
                      redeemable assets which can be converted into Resting ASC
                      automatically within a short time period when there is an
                      increased demand for converting USDS into USDC or similar
                      assets.
                      <br />
                      <br />
                      Max Latent ASC is the maximum allowed amount of Latent ASC
                      in the system to be eligible as ASC and is calculated as ⅓
                      of Resting ASC.
                    </Text>
                  }
                />
              </Flex>
            ),
            value: (
              <Flex gap="2xs" alignItems="center">
                <CurrencyValue
                  value={data.total_latent_asc}
                  options={{ currency: 'USD' }}
                />
                <span>/</span>
                <CurrencyValue
                  value={data.max_latent_asc}
                  options={{ currency: 'USD' }}
                />
              </Flex>
            )
          },
          {
            title: (
              <Flex gap="3xs">
                ASC Ratio
                <InfoTooltip
                  text={
                    <Text size="3">
                      Total ASC divided by total USDS supply.
                      <br />
                      <br />
                      The system targets ASC ratio at 25% by decreasing the
                      internal rates when the ratio is above 25% and increases
                      internal rates when the ratio is below 25%.
                    </Text>
                  }
                />
              </Flex>
            ),
            value: <PercentageValue value={data.asc_share} />
          }
        ]}
      />
      <ASCGraph />
      <ASCTable />
    </Page>
  );
}
